import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";

export default function SyncDialog({ open, onOpenChange, currentSync, onRefresh }) {
    const [otp, setOtp] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) setOtp("");
    }, [open]);

    const submitOtp = async () => {
        if (!currentSync?.id) return;
        setSubmitting(true);
        try {
            await api.post(`/sync/${currentSync.id}/otp`, { otp });
            toast.success("Code envoyé");
            setOtp("");
            onRefresh?.();
        } catch (e) {
            toast.error("Erreur lors de l'envoi du code");
        } finally {
            setSubmitting(false);
        }
    };

    const cancel = async () => {
        if (!currentSync?.id) return;
        try {
            await api.post(`/sync/${currentSync.id}/cancel`);
            toast.success("Synchronisation annulée");
            onOpenChange(false);
        } catch (e) {
            toast.error("Impossible d'annuler");
        }
    };

    const status = currentSync?.status || "pending";
    const isRunning = ["pending", "running", "awaiting_otp"].includes(status);
    const isDone = status === "success";
    const isFailed = status === "failed" || status === "cancelled";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="sync-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight">
                        Synchronisation Amazon
                    </DialogTitle>
                    <DialogDescription>
                        {isRunning && "Récupération de vos commandes en cours…"}
                        {isDone && "Synchronisation terminée avec succès."}
                        {isFailed && "La synchronisation n'a pas pu aboutir."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="flex items-center gap-3">
                        {isRunning && <CircleNotch size={22} className="animate-spin text-primary" />}
                        {isDone && <CheckCircle size={22} weight="fill" className="text-emerald-600" />}
                        {isFailed && <XCircle size={22} weight="fill" className="text-destructive" />}
                        <span className="text-sm font-semibold capitalize" data-testid="sync-status-label">
                            {status === "awaiting_otp" ? "code requis" : status}
                        </span>
                    </div>

                    <Progress value={currentSync?.progress || 0} />

                    <div className="text-sm text-muted-foreground" data-testid="sync-message">
                        {currentSync?.message || "—"}
                    </div>

                    {status === "awaiting_otp" && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3">
                            <div className="flex items-center gap-2 text-amber-700">
                                <ShieldCheck size={18} weight="fill" />
                                <div className="text-sm font-semibold">Vérification Amazon</div>
                            </div>
                            <p className="text-xs text-amber-700">
                                Amazon vient de vous envoyer un code à 6 chiffres par SMS ou email.
                                Saisissez-le ci-dessous&nbsp;:
                            </p>
                            <div className="space-y-2">
                                <Label htmlFor="otp-input">Code OTP</Label>
                                <Input
                                    id="otp-input"
                                    inputMode="numeric"
                                    maxLength={8}
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                    placeholder="123456"
                                    className="font-mono text-lg tracking-widest text-center"
                                    data-testid="otp-input"
                                />
                                <Button
                                    onClick={submitOtp}
                                    disabled={!otp || submitting}
                                    className="w-full bg-primary hover:bg-primary/90"
                                    data-testid="otp-submit"
                                >
                                    {submitting ? "Envoi…" : "Valider le code"}
                                </Button>
                            </div>
                        </div>
                    )}

                    {currentSync?.invoices_found > 0 && (
                        <div className="text-xs text-muted-foreground">
                            {currentSync.invoices_found} factures récupérées jusqu'ici.
                        </div>
                    )}

                    {isRunning && (
                        <Button
                            variant="outline"
                            onClick={cancel}
                            className="w-full"
                            data-testid="sync-cancel-btn"
                        >
                            Annuler
                        </Button>
                    )}
                    {(isDone || isFailed) && (
                        <Button
                            onClick={() => onOpenChange(false)}
                            className="w-full"
                            data-testid="sync-close-btn"
                        >
                            Fermer
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
